import cx from 'classnames'
import { Link } from 'gatsby'
import React, { ReactNode } from 'react'
import * as styles from '../styles/components/crosshair-link.module.scss'
import { CrosshairsClosedIcon, CrosshairsOpenIcon } from './icons/crosshairs'

type LinkType = 'externalLink' | 'internalLink' | 'button'

interface CrosshairLinkCommonProps {
    href?: string
    as: LinkType
    label: string
    colorVariant?: 'light' | 'dark'
    hoverStyleType?: 'default' | 'boldOnHover'
    openInNewTab?: boolean
    onClick?: () => void
    disabled?: boolean
    type?: HTMLButtonElement['type']
    className?: string
}

export interface CrosshairLinkHrefProps extends CrosshairLinkCommonProps {
    href: string
    as: 'externalLink' | 'internalLink'
}

export interface CrosshairLinkButtonProps extends CrosshairLinkCommonProps {
    href?: undefined
    as: 'button'
}

export type CrosshairLinkProps = CrosshairLinkHrefProps | CrosshairLinkButtonProps

type LinkWrapperProps = CrosshairLinkProps & {
    children?: ReactNode
}

// Gatsby's Link prefetches page resources to decrease load times when navigating to internal pages
// but should not be used for external links
// https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-link/
const LinkWrapper = ({ as, children, href, onClick, className, label, openInNewTab }: LinkWrapperProps) => {
    const commonProps = {
        className,
        onClick,
        'aria-label': label,
        target: openInNewTab ? '_blank' : undefined,
    }

    switch (as) {
    case 'internalLink':
        return <Link to={href} {...commonProps}>{children}</Link>

    case 'button':
        return <button {...commonProps}>{children}</button>

    default:
        return <a href={href} {...commonProps}>{children}</a>
    }
}

// Note: due to how Safari on iOS treats non-opaque anchor tags, this won't work properly
// when used e.g. inside a container which is faded in
const CrosshairLink = (props: CrosshairLinkProps) => {
    const { label, href, as, className, colorVariant, hoverStyleType, disabled = false, onClick, ...rest } = props
    return (
        <LinkWrapper {...props}>
            <span
                {...rest}
                className={cx(styles.contentContainer, className, {
                    [styles.disabled]: disabled,
                })}
            >
                <div className={styles.crosshairsContainer}>
                    <CrosshairsOpenIcon
                        className={cx(styles.crosshairs, styles.crosshairsOpen, {
                            [styles.white]: colorVariant === 'light',
                        })}
                    />
                    <CrosshairsClosedIcon
                        className={cx(
                            styles.crosshairs,
                            styles.crosshairsClosed,
                            {
                                [styles.white]: colorVariant === 'light',
                                [styles.boldOnHover]: hoverStyleType === 'boldOnHover',
                            })}
                    />
                </div>
                <span
                    className={cx(
                        styles.linkText,
                        {
                            [styles.white]: colorVariant === 'light',
                            [styles.boldOnHover]: hoverStyleType === 'boldOnHover',
                        })}
                >
                    {label}
                </span>
            </span>
        </LinkWrapper>
    )
}

export default CrosshairLink
